import React, { useState, useEffect } from 'react';
import ItemEditor from './ItemEditor';
import StatOverview from './StatOverview';
import { sumStat, statsColumnsMapping, statAsString } from '../utils/stats';
import { calculateItemStats } from '../utils/item';
import baseStats from '../../data/base_stats.json';
import prefixesStatsRaw from '../../data/prefixes_stats.json';
import suffixesStatsRaw from '../../data/suffixes_stats.json';
import excludedPrefixes from '../../data/excluded_prefixes.json';
import excludedSuffixes from '../../data/excluded_suffixes.json';

// Process prefixes and suffixes
const processedPrefixes = Object.entries(prefixesStatsRaw)
    .filter(([id]) => !excludedPrefixes.includes(parseInt(id)))
    .map(([id, stats]) => ({
        id: parseInt(id),
        stats: Object.values(stats)
    }));

const processedSuffixes = Object.entries(suffixesStatsRaw)
    .filter(([id]) => !excludedSuffixes.includes(parseInt(id)))
    .map(([id, stats]) => ({
        id: parseInt(id),
        stats: Object.values(stats)
    }));

// Process base stats by category
const processedBaseStats = {};
Object.entries(baseStats).forEach(([key, stats]) => {
    const [category] = key.split('-');
    if (!processedBaseStats[category]) {
        processedBaseStats[category] = {};
    }
    processedBaseStats[category][key] = stats;
});

// Initialize global data
window.charPlannerData = {
    baseStats: processedBaseStats,
    prefixesStats: processedPrefixes,
    suffixesStats: processedSuffixes,
    i18n: {} // You can load translations here if needed
};

const CharPlanner = () => {
    console.log('CharPlanner component initializing...');
    console.log('window.charPlannerData:', window.charPlannerData);

    const [showDetailedResults, setShowDetailedResults] = useState(false);
    const [profileLink, setProfileLink] = useState('');
    const [profileDataLoading, setProfileDataLoading] = useState(false);
    const [playerStats, setPlayerStats] = useState({
        level: 1,
        health: 0,
        strength: { base: 5, max: 9, throughItems: 0, total: 5 },
        dexterity: { base: 5, max: 9, throughItems: 0, total: 5 },
        agility: { base: 5, max: 9, throughItems: 0, total: 5 },
        constitution: { base: 5, max: 9, throughItems: 0, total: 5 },
        charisma: { base: 5, max: 9, throughItems: 0, total: 5 },
        intelligence: { base: 5, max: 9, throughItems: 0, total: 5 },
        armor: 0,
        damage: {
            total: { low: 0, high: 2 },
            base: { low: 0, high: 2 },
            throughItems: 0,
            throughStrength: 0
        },
        resilience: {
            ceiling: 0,
            total: 0,
            throughItems: 0,
            throughAgility: 0,
        },
        avoidCriticalHitChance: 0,
        blocking: {
            ceiling: 0,
            total: 0,
            throughItems: 0,
            throughStrength: 0,
        },
        blockingChance: 0,
        criticalHit: {
            ceiling: 0,
            total: 0,
            throughItems: 0,
            throughDexterity: 0,
        },
        criticalHitChance: 0,
        threat: {
            total: 0,
            throughItems: 0,
            throughCharisma: 0,
        },
        healing: {
            total: 0,
            throughItems: 0,
            throughIntelligence: 0,
        },
        criticalHealing: {
            total: 0,
            ceiling: 0,
            throughItems: 0,
            throughIntelligence: 0,
        },
        criticalHealingChance: 0,
    });
    const [playerItems, setPlayerItems] = useState(Array(14).fill(null).map(() => ({
        prefixId: null,
        base: null,
        suffixId: null,
        quality: 0,
        bonus: null,
        bonusValue: 0,
        stats: {}
    })));

    const calculateResults = () => {
        console.log('Starting calculateResults');
        const cap = (num, cap) => num > cap ? cap : num;
        const leftCap = (num, cap) => num < cap ? cap : num;

        // Calculate stats from items
        const items = playerItems.map((item, index) => {
            console.log(`Processing item ${index}:`, item);
            if (!item || !item.base) return null;
            return calculateItemStats(item.prefixId, item.base, item.suffixId, item.quality);
        }).filter(Boolean);

        console.log('Processed items:', items);

        const itemsStatsArr = items.map(item => item.stats).filter(v => Object.keys(v).length > 0);
        console.log('Items stats array:', itemsStatsArr);

        // Update player stats
        setPlayerStats(prev => {
            const newStats = { ...prev };

            // Basic stats
            ["strength", "dexterity", "agility", "constitution", "charisma", "intelligence"].forEach(stat => {
                newStats[stat].max = newStats[stat].base + Math.round(newStats[stat].base / 2) + newStats.level;
                newStats[stat].throughItems = sumStat(itemsStatsArr, statsColumnsMapping[stat]);

                // Additional bonuses applied to items
                newStats[stat].throughItems += playerItems
                    .filter(item => item && item.bonus === stat)
                    .reduce((acc, item) => acc + (item.bonusValue || 0), 0);

                // Percentage bonuses
                newStats[stat].throughItems += itemsStatsArr.reduce(
                    (acc, item) => acc + Math.round(item[statsColumnsMapping[stat + "Percentage"]] * newStats[stat].base / 100),
                    0
                );

                newStats[stat].total = cap(newStats[stat].base + newStats[stat].throughItems, newStats[stat].max);
            });

            // Health
            newStats.health = (newStats.level * 25) + (newStats.constitution.total * 25) - 50 + sumStat(itemsStatsArr, statsColumnsMapping.health);

            // Damage
            const weapon = items.find(i => i.isWeapon) || null;
            newStats.damage = {
                base: {
                    low: weapon ? weapon.damageLow : 0,
                    high: weapon ? weapon.damageHigh : 2
                },
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.damage) - (weapon ? weapon.stats[statsColumnsMapping.damage] : 0),
                throughStrength: Math.floor(newStats.strength.total / 10)
            };

            // +damage bonus on weapon
            const weaponItem = playerItems[3];
            if (weaponItem && weaponItem.bonus === 'damage') {
                newStats.damage.throughItems += weaponItem.bonusValue || 0;
            }

            newStats.damage.total = {
                low: leftCap(newStats.damage.base.low + newStats.damage.throughItems + newStats.damage.throughStrength, 0),
                high: leftCap(newStats.damage.base.high + newStats.damage.throughItems + newStats.damage.throughStrength, 2)
            };

            // Armor
            newStats.armor = sumStat(itemsStatsArr, statsColumnsMapping.armor);
            newStats.armor += playerItems
                .filter(item => item && item.bonus === "armor")
                .reduce((acc, item) => acc + (item.bonusValue || 0), 0);

            // Resilience
            newStats.resilience = {
                ceiling: Math.floor(98 * (newStats.level - 8) / 52) + 1,
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.hardening),
                throughAgility: Math.floor(newStats.agility.total / 10)
            };
            newStats.resilience.total = newStats.resilience.throughItems + newStats.resilience.throughAgility;
            newStats.avoidCriticalHitChance = cap(Math.round(newStats.resilience.total * 52 / (newStats.level - 8) / 4), 25);

            // Blocking
            newStats.blocking = {
                ceiling: Math.floor(49.5 * 6 * (newStats.level - 8) / 52) + 1,
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.blocking),
                throughStrength: Math.floor(newStats.strength.total / 10)
            };
            newStats.blocking.total = newStats.blocking.throughItems + newStats.blocking.throughStrength;
            newStats.blockingChance = cap(Math.round(newStats.blocking.total * 52 / (newStats.level - 8) / 6), 50);

            // Critical hit
            newStats.criticalHit = {
                ceiling: Math.floor(49.5 * 5 * (newStats.level - 8) / 52) + 1,
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.criticalAttack),
                throughDexterity: Math.floor(newStats.dexterity.total / 10)
            };
            newStats.criticalHit.total = newStats.criticalHit.throughItems + newStats.criticalHit.throughDexterity;
            newStats.criticalHitChance = cap(Math.round((newStats.criticalHit.total * 52 / (newStats.level - 8)) / 5), 50);

            // Threat
            newStats.threat = {
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.threat),
                throughCharisma: Math.floor(newStats.charisma.total * 0.7)
            };
            newStats.threat.total = newStats.threat.throughItems + newStats.threat.throughCharisma;

            // Healing
            newStats.healing = {
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.healing),
                throughIntelligence: Math.floor(newStats.intelligence.total * 0.8)
            };
            newStats.healing.total = newStats.healing.throughItems + newStats.healing.throughIntelligence;

            // Critical healing
            newStats.criticalHealing = {
                ceiling: Math.floor(49.5 * 5 * (newStats.level - 8) / 52) + 1,
                throughItems: sumStat(itemsStatsArr, statsColumnsMapping.criticalHealing),
                throughIntelligence: Math.floor(newStats.intelligence.total / 10)
            };
            newStats.criticalHealing.total = newStats.criticalHealing.throughItems + newStats.criticalHealing.throughIntelligence;
            newStats.criticalHealingChance = cap(Math.round((newStats.criticalHealing.total * 52 / (newStats.level - 8)) / 5), 50);

            console.log('New player stats:', newStats);
            return newStats;
        });
    };

    useEffect(() => {
        console.log('Level changed, recalculating...');
        calculateResults();
    }, [playerStats.level]);

    useEffect(() => {
        console.log('Base stats changed, recalculating...');
        calculateResults();
    }, [
        playerStats.strength.base,
        playerStats.dexterity.base,
        playerStats.agility.base,
        playerStats.constitution.base,
        playerStats.charisma.base,
        playerStats.intelligence.base
    ]);

    useEffect(() => {
        console.log('Items changed, recalculating...', playerItems);
        calculateResults();
    }, [playerItems]);

    const updateItem = (itemId, item) => {
        console.log('Updating item in CharPlanner:', { itemId, item });
        setPlayerItems(prev => {
            const newItems = [...prev];
            newItems[itemId] = item;
            console.log('New items array:', newItems);
            return newItems;
        });
    };

    const loadUserData = async () => {
        setProfileDataLoading(true);
        try {
            const url = new URL(profileLink);
            const server = url.host.match(/s\d+-[a-z]+/)?.[0];
            const playerId = url.searchParams.get('p');
            const doll = url.searchParams.get('doll') || '1';

            if (!server || !playerId?.match(/\d+/)) {
                alert('Profile link is not valid');
                return;
            }

            const response = await fetch(`https://fetch-user-profile.c00k3rr.workers.dev/?server=${server}&user_id=${playerId}&doll=${doll}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const profileData = await response.json();
            console.log('Raw profile data from server:', profileData);

            // Update player stats
            setPlayerStats(prev => {
                const newStats = {
                    ...prev,
                    level: parseInt(profileData.level) || prev.level,
                    strength: { ...prev.strength, base: parseInt(profileData.strength) || prev.strength.base },
                    dexterity: { ...prev.dexterity, base: parseInt(profileData.dexterity) || prev.dexterity.base },
                    agility: { ...prev.agility, base: parseInt(profileData.agility) || prev.agility.base },
                    constitution: { ...prev.constitution, base: parseInt(profileData.constitution) || prev.constitution.base },
                    charisma: { ...prev.charisma, base: parseInt(profileData.charisma) || prev.charisma.base },
                    intelligence: { ...prev.intelligence, base: parseInt(profileData.intelligence) || prev.intelligence.base }
                };
                console.log('Updated player stats:', newStats);
                return newStats;
            });

            // Update items
            if (profileData.items) {
                setPlayerItems(prev => {
                    const newItems = [...prev];
                    console.log('Processing items from server:', profileData.items);
                    
                    Object.entries(profileData.items).forEach(([slot, itemData]) => {
                        console.log(`Processing item for slot ${slot}:`, itemData);
                        const slotIndex = parseInt(slot);
                        if (!isNaN(slotIndex) && slotIndex >= 0 && slotIndex < newItems.length) {
                            newItems[slotIndex] = {
                                prefixId: itemData.prefix_id ? parseInt(itemData.prefix_id) : null,
                                base: itemData.base || null,
                                suffixId: itemData.suffix_id ? parseInt(itemData.suffix_id) : null,
                                quality: parseInt(itemData.quality) || 0,
                                bonus: itemData.bonus || null,
                                bonusValue: parseInt(itemData.bonusValue) || 0,
                                stats: {}
                            };
                            console.log(`Updated item in slot ${slotIndex}:`, newItems[slotIndex]);
                        }
                    });
                    
                    console.log('Final items array:', newItems);
                    return newItems;
                });
            }

            calculateResults();
        } catch (error) {
            console.error('Error loading profile:', error);
            alert('Failed to load profile data. Please try again later.');
        } finally {
            setProfileDataLoading(false);
        }
    };

    return (
        <main className="w-full flex-grow py-4 px-2 sm:p-6">
            <h1 className="text-3xl text-black pb-6">{window.charPlannerData.i18n['Character planner']}</h1>

            <div className="bg-white py-3 px-2 sm:p-4 rounded-md">
                <div className="bg-gray-100 rounded w-full p-4 px-5 mb-4 flex flex-col sm:flex-row">
                    <label className="flex sm:gap-4 flex-1 sm:mr-2 sm:items-center flex-col sm:flex-row">
                        <span className="min-w-fit">{window.charPlannerData.i18n['Profile link']}:</span>
                        <span className="spinner block" style={{ display: profileDataLoading ? 'block' : 'none' }}></span>
                        <input 
                            type="url"
                            className="w-full p-2 px-4 bg-gray-200 disabled:animate-pulse disabled:opacity-10 disabled:cursor-not-allowed"
                            value={profileLink}
                            onChange={(e) => setProfileLink(e.target.value)}
                            disabled={profileDataLoading}
                        />
                    </label>
                    <button
                        className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-30 disabled:cursor-not-allowed mt-2 sm:mt-0"
                        onClick={loadUserData}
                        disabled={!profileLink || profileDataLoading}
                    >
                        {window.charPlannerData.i18n['Load']}
                    </button>
                </div>

                <div 
                    className={`flex items-start flex-col gap-y-2 lg:flex-row ${!showDetailedResults ? 'md:flex-row' : ''}`}
                >
                    <span className="md:w-1/3 lg:w-1/4 xl:w-1/5 hidden"></span>
                    <div 
                        className={`bg-gray-100 rounded w-full lg:w-1/2 xl:w-1/3 p-4 px-5 grid grid-cols-1 gap-4 ${
                            showDetailedResults ? 'sm:grid-cols-2' : 'md:w-1/3 lg:w-1/4 xl:w-1/5'
                        }`}
                    >
                        <div className="flex flex-col gap-y-2">
                            <label>
                                <input 
                                    type="checkbox" 
                                    checked={showDetailedResults}
                                    onChange={(e) => setShowDetailedResults(e.target.checked)}
                                /> 
                                Detailed
                            </label>
                            <div className="flex items-center">
                                <div className="flex-1">{window.charPlannerData.i18n['Level']}</div>
                                <input 
                                    type="number" 
                                    value={playerStats.level}
                                    onChange={(e) => setPlayerStats(prev => ({ ...prev, level: parseInt(e.target.value) }))}
                                    className="text-sm w-16 bg-gray-200 p-1 pl-2"
                                />
                            </div>

                            <StatOverview 
                                stat={playerStats.strength}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Strength']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        strength: { ...prev.strength, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                            <StatOverview 
                                stat={playerStats.dexterity}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Dexterity']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        dexterity: { ...prev.dexterity, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                            <StatOverview 
                                stat={playerStats.agility}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Agility']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        agility: { ...prev.agility, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                            <StatOverview 
                                stat={playerStats.constitution}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Constitution']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        constitution: { ...prev.constitution, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                            <StatOverview 
                                stat={playerStats.charisma}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Charisma']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        charisma: { ...prev.charisma, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                            <StatOverview 
                                stat={playerStats.intelligence}
                                detailed={showDetailedResults}
                                name={window.charPlannerData.i18n['Intelligence']}
                                level={playerStats.level}
                                onUpdate={(value) => {
                                    setPlayerStats(prev => ({
                                        ...prev,
                                        intelligence: { ...prev.intelligence, base: value }
                                    }));
                                    calculateResults();
                                }}
                            />
                        </div>

                        <div className="flex flex-col gap-y-2">
                            <div className="flex font-bold">
                                <div className="flex-1">{window.charPlannerData.i18n['Armor']}</div>
                                <div>{playerStats.armor}</div>
                            </div>

                            {showDetailedResults && (
                                <div className="flex text-gray-400 text-xs">
                                    <div className="flex-1">{window.charPlannerData.i18n['Absorbed damage']}</div>
                                    <div>
                                        {Math.ceil((playerStats.armor / 74) - (playerStats.armor / 74) / 660 + 1)}
                                        -
                                        {Math.floor(playerStats.armor / 66 + playerStats.armor / 660)}
                                    </div>
                                </div>
                            )}

                            <div className="flex text-sm">
                                <div className="flex-1">{window.charPlannerData.i18n['Resilience']}</div>
                                <div className={playerStats.resilience.total >= playerStats.resilience.ceiling ? 'text-green-500' : 'text-red-500'}>
                                    {playerStats.resilience.total} / {playerStats.resilience.ceiling}
                                </div>
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.resilience.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through agility']}</div>
                                        {playerStats.resilience.throughAgility}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Chance of avoiding critical hits']}</div>
                                        {playerStats.avoidCriticalHitChance}%
                                    </div>
                                </>
                            )}

                            <div className="flex text-sm">
                                <div className="flex-1">{window.charPlannerData.i18n['Blocking value']}</div>
                                <div className={playerStats.blocking.total >= playerStats.blocking.ceiling ? 'text-green-500' : 'text-red-500'}>
                                    {playerStats.blocking.total} / {playerStats.blocking.ceiling}
                                </div>
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.blocking.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through strength']}</div>
                                        {playerStats.blocking.throughStrength}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Chance of blocking']}</div>
                                        {playerStats.blockingChance}%
                                    </div>
                                </>
                            )}

                            <div className="flex font-bold">
                                <div className="flex-1">{window.charPlannerData.i18n['Damage']}</div>
                                {playerStats.damage.total.low} - {playerStats.damage.total.high}
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Base']}</div>
                                        {playerStats.damage.base.low} - {playerStats.damage.base.high}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.damage.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through strength']}</div>
                                        {playerStats.damage.throughStrength}
                                    </div>
                                </>
                            )}

                            <div className="flex text-sm">
                                <div className="flex-1">{window.charPlannerData.i18n['Critical damage']}</div>
                                <div className={playerStats.criticalHit.total >= playerStats.criticalHit.ceiling ? 'text-green-500' : 'text-red-500'}>
                                    {playerStats.criticalHit.total} / {playerStats.criticalHit.ceiling}
                                </div>
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.criticalHit.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through dexterity']}</div>
                                        {playerStats.criticalHit.throughDexterity}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Chance for critical damage']}</div>
                                        {playerStats.criticalHitChance}%
                                    </div>
                                </>
                            )}

                            <div className="flex font-bold">
                                <div className="flex-1">{window.charPlannerData.i18n['Threat']}</div>
                                {playerStats.threat.total}
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.threat.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through charisma']}</div>
                                        {playerStats.threat.throughCharisma}
                                    </div>
                                </>
                            )}

                            <div className="flex font-bold">
                                <div className="flex-1">{window.charPlannerData.i18n['Healing']}</div>
                                {playerStats.healing.total}
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.healing.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through intelligence']}</div>
                                        {playerStats.healing.throughIntelligence}
                                    </div>
                                </>
                            )}

                            <div className="flex text-sm">
                                <div className="flex-1">{window.charPlannerData.i18n['Critical healing value']}</div>
                                <div className={playerStats.criticalHealing.total >= playerStats.criticalHealing.ceiling ? 'text-green-500' : 'text-red-500'}>
                                    {playerStats.criticalHealing.total} / {playerStats.criticalHealing.ceiling}
                                </div>
                            </div>
                            {showDetailedResults && (
                                <>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through items']}</div>
                                        {playerStats.criticalHealing.throughItems}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Through intelligence']}</div>
                                        {playerStats.criticalHealing.throughIntelligence}
                                    </div>
                                    <div className="flex text-gray-400 text-xs">
                                        <div className="flex-1">{window.charPlannerData.i18n['Chance of critical healing']}</div>
                                        {playerStats.criticalHealingChance}%
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-y-2 w-full lg:flex-1 lg:ml-4">
                        <div className="w-full flex gap-y-2 flex-col lg:gap-x-2 lg:flex-row lg:justify-end">
                            <div className="hidden lg:block lg:w-1/3"></div>
                            <ItemEditor
                                item={playerItems[2]}
                                baseCategory={4}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(2, item)}
                            />
                            <ItemEditor
                                item={playerItems[11]}
                                baseCategory={9}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(11, item)}
                            />
                        </div>
                        <div className="w-full flex gap-y-2 flex-col lg:gap-x-2 lg:flex-row lg:justify-end">
                            <ItemEditor
                                item={playerItems[0]}
                                baseCategory={1}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(0, item)}
                            />
                            <ItemEditor
                                item={playerItems[1]}
                                baseCategory={2}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(1, item)}
                            />
                            <ItemEditor
                                item={playerItems[3]}
                                baseCategory={5}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(3, item)}
                            />
                        </div>
                        <div className="w-full flex gap-y-2 flex-col lg:gap-x-2 lg:flex-row lg:justify-end">
                            <ItemEditor
                                item={playerItems[4]}
                                baseCategory={6}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(4, item)}
                            />
                            <ItemEditor
                                item={playerItems[5]}
                                baseCategory={7}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(5, item)}
                            />
                            <ItemEditor
                                item={playerItems[6]}
                                baseCategory={8}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(6, item)}
                            />
                        </div>
                        <div className="w-full flex gap-y-2 flex-col lg:gap-x-2 lg:flex-row lg:justify-end">
                            <ItemEditor
                                item={playerItems[7]}
                                baseCategory={10}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(7, item)}
                            />
                            <ItemEditor
                                item={playerItems[8]}
                                baseCategory={11}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(8, item)}
                            />
                            <ItemEditor
                                item={playerItems[9]}
                                baseCategory={12}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(9, item)}
                            />
                        </div>
                        <div className="w-full flex gap-y-2 flex-col lg:gap-x-2 lg:flex-row lg:justify-end">
                            <ItemEditor
                                item={playerItems[10]}
                                baseCategory={13}
                                prefixesStats={window.charPlannerData.prefixesStats}
                                suffixesStats={window.charPlannerData.suffixesStats}
                                onUpdate={(item) => updateItem(10, item)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default CharPlanner; 